import { Injectable } from '@angular/core';
import { Router, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../service/auth.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserEventRole } from '../enum/user.event.role';

@Injectable()
export class ChairOrAdminGuard implements CanActivateChild {
  constructor(public authService: AuthService, public router: Router) { }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const eventId = route.params.eventId || route.parent.params.eventId;

    return forkJoin([
      this.authService.directUserIsChair(eventId),
      this.authService.directUserIsUserEvent(eventId, UserEventRole.PUBLICATION_CHAIR),
    ]).pipe(map(([isChair, isPubChair]) => {
      // Chair do evento (ou admin, que `directUserIsChair` já cobre): área toda.
      if (isChair) { return true; }

      // Publication chair: SÓ as rotas marcadas com `data.publicationChair`.
      //
      // Antes bastava ser publication chair para navegar em `/admin/{id}/**`
      // INTEIRO — a única barreira era o sidebar esconder os itens, o que
      // some ao digitar a URL na barra de endereços. Foi assim que se chegou
      // à página de membros do comitê e se adicionou como chair (issue
      // #2381). Esconder item de menu não é controle de acesso.
      //
      // O backend continua sendo a garantia real, e é lá que a escalação foi
      // fechada; isto é defesa em profundidade, para o front não oferecer
      // navegação que ele não deveria.
      if (isPubChair) {
        if (this.routeAllowsPublicationChair(route)) { return true; }
        // Manda para a área que lhe pertence, em vez de expulsar do evento.
        this.router.navigate(['/admin', eventId, 'publication']);
        return false;
      }

      this.router.navigate(['/']);
      return false;
    }));
  }

  /**
   * A rota (ou algum ancestral dela) está marcada como acessível ao
   * publication chair?
   *
   * Percorre a cadeia porque `canActivateChild` recebe o snapshot do filho
   * que está sendo ativado, e rotas com filhos próprios carregam o `data` no
   * nível de cima. Default NEGA: rota nova na área do chair nasce fechada
   * para o publication chair, e só se abre por marcação explícita.
   */
  private routeAllowsPublicationChair(route: ActivatedRouteSnapshot): boolean {
    let node: ActivatedRouteSnapshot | null = route;
    while (node) {
      if (node.data && node.data.publicationChair === true) { return true; }
      node = node.firstChild;
    }
    return false;
  }
}
